import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { RequestAPI } from '../models/HttpBase/request-api';
import { ResponseAPI } from '../models/HttpBase/response-api';
import { SKIP_GLOBAL_LOADER } from '../interceptor/my-http-interceptor.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class HttpClientService {
  private readonly errorLogin = new Subject<boolean>();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService
  ) {}

  public get<T>(
    path: string,
    options?: { params?: HttpParams; context?: HttpContext }
  ): Observable<ResponseAPI<T>> {
    return this.httpClient
      .get<ResponseAPI<T>>(this.resolveUrl(path), options)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public put<T>(path: string, content: object = {}): Observable<ResponseAPI<T>> {
    const request = this.buildRequest(content, true);
    return this.httpClient
      .put<ResponseAPI<T>>(this.resolveUrl(path), request)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public post<T>(path: string, content: object = {}): Observable<ResponseAPI<T>> {
    const request = this.buildRequest(content, true);
    return this.httpClient
      .post<ResponseAPI<T>>(this.resolveUrl(path), request)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public postAnonymous<T>(path: string, content: object = {}): Observable<ResponseAPI<T>> {
    const request = this.buildRequest(content, false);
    return this.httpClient
      .post<ResponseAPI<T>>(this.resolveUrl(path), request)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public putAnonymous<T>(path: string, content: object = {}): Observable<ResponseAPI<T>> {
    const request = this.buildRequest(content, false);
    return this.httpClient
      .put<ResponseAPI<T>>(this.resolveUrl(path), request)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public delete<T>(path: string): Observable<ResponseAPI<T>> {
    return this.httpClient
      .delete<ResponseAPI<T>>(this.resolveUrl(path))
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public postFile<T>(path: string, formData: FormData): Observable<ResponseAPI<T>> {
    return this.httpClient
      .post<ResponseAPI<T>>(this.resolveUrl(path), formData)
      .pipe(
        map((response) => this.hydrateResponse(response)),
        catchError((error: HttpErrorResponse) => this.formatErrors(error))
      );
  }

  public postExcel<T>(
    path: string,
    body: unknown,
    options?: Record<string, unknown>
  ): Observable<T> {
    return this.httpClient
      .post<T>(this.resolveUrl(path), body, options)
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public showCredError(): void {
    this.errorLogin.next(true);
  }

  public hideCredError(): void {
    this.errorLogin.next(false);
  }

  public getBlob(path: string): Observable<Blob> {
    return this.httpClient
      .get(this.resolveUrl(path), { responseType: 'blob' })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public getBlobWithHeaders(path: string): Observable<HttpResponse<Blob>> {
    return this.httpClient
      .get(this.resolveUrl(path), {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public getArrayBuffer(path: string): Observable<ArrayBuffer> {
    return this.httpClient
      .get(this.resolveUrl(path), { responseType: 'arraybuffer' })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public getSilent<T>(
    url: string,
    options: { context?: HttpContext } & Record<string, unknown> = {}
  ): Observable<T> {
    const context = options.context ?? this.noLoaderContext();
    return this.httpClient
      .get<T>(this.resolveUrl(url), { ...options, context })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public postSilent<T>(
    url: string,
    body: unknown,
    options: { context?: HttpContext; includeCredentials?: boolean } & Record<string, unknown> = {}
  ): Observable<T> {
    const context = options.context ?? this.noLoaderContext();
    const request = this.buildRequest(body, options.includeCredentials ?? true);

    return this.httpClient
      .post<T>(this.resolveUrl(url), request, { ...options, context })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  public putSilent<T>(
    url: string,
    body: unknown,
    options: { context?: HttpContext; includeCredentials?: boolean } & Record<string, unknown> = {}
  ): Observable<T> {
    const context = options.context ?? this.noLoaderContext();
    const request = this.buildRequest(body, options.includeCredentials ?? true);

    return this.httpClient
      .put<T>(this.resolveUrl(url), request, { ...options, context })
      .pipe(catchError((error: HttpErrorResponse) => this.formatErrors(error)));
  }

  private getBaseURL(): string {
    return environment.URL_BASE;
  }

  private resolveUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = this.getBaseURL().replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${base}/${normalizedPath}`;
  }

  private buildRequest<T>(content: T, includeCredentials: boolean): RequestAPI<T> {
    const request = new RequestAPI<T>();
    request.UserCrendential = includeCredentials ? this.authService.getUserCredentials() : null;
    request.ObjectRequest = content;
    return request;
  }

  private formatErrors(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.showCredError();
    }
    return throwError(() => error.error ?? error);
  }

  private noLoaderContext(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_LOADER, true);
  }

  private hydrateResponse<T>(response: ResponseAPI<T>): ResponseAPI<T> {
    return Object.assign(new ResponseAPI<T>(), response);
  }
}
